package com.schoolconnect.app.feature.schoolconfig

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import com.schoolconnect.app.designsystem.AppBackground
import com.schoolconnect.app.designsystem.AppPageHeader
import com.schoolconnect.app.designsystem.AppPageSubtitle
import com.schoolconnect.app.designsystem.AppDanger
import com.schoolconnect.app.designsystem.AppLabeledDropdownField
import com.schoolconnect.app.designsystem.AppPrimary
import com.schoolconnect.app.designsystem.AppPrimarySoft
import com.schoolconnect.app.designsystem.AppSuccess
import com.schoolconnect.app.designsystem.AppText
import com.schoolconnect.app.designsystem.list.AdminListFooter
import com.schoolconnect.app.designsystem.list.AdminListHeaderColumn
import com.schoolconnect.app.designsystem.list.AdminListMessageAndFilters
import com.schoolconnect.app.designsystem.list.AdminListPageScaffold
import com.schoolconnect.app.designsystem.list.AdminListSortColumn
import com.schoolconnect.app.designsystem.list.AdminListSortHeaderRow
import com.schoolconnect.app.designsystem.list.AdminListTableBody
import com.schoolconnect.app.designsystem.list.AdminListTableBodyText
import com.schoolconnect.app.designsystem.list.AdminListTableCard
import com.schoolconnect.app.designsystem.list.AdminListTableShimmer
import com.schoolconnect.app.designsystem.list.AdminListToolbar
import com.schoolconnect.app.designsystem.list.AdminListToolbarButton
import com.schoolconnect.app.designsystem.list.AdminStatusChip
import com.schoolconnect.app.designsystem.list.onViewDoubleClick
import com.schoolconnect.app.feature.common.ShellTopRightActions
import com.schoolconnect.app.feature.common.pageFooterText
import com.schoolconnect.app.feature.common.pageWindow
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.edit
import ischoolconnect.sharedui.generated.resources.eye
import ischoolconnect.sharedui.generated.resources.info_circle
import ischoolconnect.sharedui.generated.resources.refresh_cw
import org.jetbrains.compose.resources.painterResource

private val SchoolConfigBlue = Color(0xFF2563EB)
private val SchoolConfigNavy = Color(0xFF08115A)
private val SchoolConfigMuted = Color(0xFF59699F)
private val SchoolConfigBorder = Color(0xFFE6EAF4)

@Composable
fun SchoolConfigScreen(
    state: SchoolConfigUiState,
    onEvent: (SchoolConfigUiEvent) -> Unit,
    roleLabel: String,
    roleSubtitle: String,
    onChangeRole: () -> Unit,
    onLogout: () -> Unit,
    onBack: () -> Unit
) {
    val window = pageWindow(state.pageIndex, state.pageSize, state.totalCount)
    var rowsPerPageExpanded by remember { mutableStateOf(false) }
    var accountMenuExpanded by remember { mutableStateOf(false) }
    val (activeConfigs, inactiveConfigs) = when (state.statusFilter) {
        SchoolConfigStatusFilter.ACTIVE -> state.totalCount to 0
        SchoolConfigStatusFilter.INACTIVE -> 0 to state.totalCount
        SchoolConfigStatusFilter.ALL ->
            state.rows.count { it.isActive } to state.rows.count { !it.isActive }
    }
    val configSubtitle = remember(activeConfigs, inactiveConfigs) {
        buildAnnotatedString {
            append("Manage school-level configuration settings. ")
            withStyle(SpanStyle(color = AppSuccess, fontWeight = FontWeight.Bold)) {
                append("$activeConfigs Active")
            }
            append(" and ")
            withStyle(SpanStyle(color = AppDanger, fontWeight = FontWeight.Bold)) {
                append("$inactiveConfigs Inactive")
            }
            append(" configurations.")
        }
    }

    Box(Modifier.fillMaxSize()) {
    AdminListPageScaffold(
        title = "School Configuration",
        subtitle = "Manage school-level configuration settings",
        subtitleAnnotated = configSubtitle,
        breadcrumb = listOf("School Setup", "Configuration"),
        showSummaryCards = false,
        headerActions = {
            ShellTopRightActions(
                roleLabel = roleLabel,
                roleSubtitle = roleSubtitle,
                initials = "SA",
                expanded = accountMenuExpanded,
                onExpandedChange = { accountMenuExpanded = it },
                onChangeRole = onChangeRole,
                onLogout = onLogout
            )
        },
        backgroundColor = AppBackground,
        titleColor = AppPageHeader,
        subtitleColor = AppPageSubtitle,
        summaryCards = {},
        banner = {
            val hasError = state.errorMessage.isNotBlank()
            val chips = buildList {
                if (state.searchQuery.isNotBlank()) add("Search: ${state.searchQuery}")
                if (state.statusFilter != SchoolConfigStatusFilter.ALL) {
                    add("Status: ${state.statusFilter.displayLabel()}")
                }
            }
            AdminListMessageAndFilters(
                message = state.errorMessage.ifBlank { state.feedbackMessage },
                onDismissMessage = { onEvent(SchoolConfigUiEvent.FeedbackDismissed) },
                chips = chips,
                onClearAll = { onEvent(SchoolConfigUiEvent.FiltersReset) },
                onRemoveChip = { chip ->
                    when {
                        chip.startsWith("Search:") -> onEvent(SchoolConfigUiEvent.SearchChanged(""))
                        chip.startsWith("Status:") -> onEvent(
                            SchoolConfigUiEvent.StatusFilterChanged(SchoolConfigStatusFilter.ALL)
                        )
                        else -> onEvent(SchoolConfigUiEvent.FiltersReset)
                    }
                },
                isError = hasError,
                messageBackgroundColor = if (hasError) null else AppPrimarySoft,
                messageBorderColor = SchoolConfigBorder,
                messageContentColor = if (hasError) null else SchoolConfigNavy,
                messageAccentColor = if (hasError) null else AppPrimary,
                filterBackgroundColor = Color(0xFFF2F7FF),
                filterBorderColor = SchoolConfigBorder,
                filterContentColor = SchoolConfigNavy,
                filterAccentColor = SchoolConfigBlue,
                messageLeadingIcon = if (hasError) null else painterResource(Res.drawable.info_circle)
            )
        },
        toolbar = {
            AdminListToolbar(
                searchQuery = state.searchQuery,
                onSearchQueryChanged = { onEvent(SchoolConfigUiEvent.SearchChanged(it)) },
                searchPlaceholder = "Search by school name, code or city...",
                searchWidth = 370.dp,
                surfaceColor = Color.White,
                borderColor = SchoolConfigBorder,
                searchIconTint = SchoolConfigMuted,
                searchTextColor = SchoolConfigNavy,
                searchCursorColor = SchoolConfigBlue,
                inlineContent = {
                    AppLabeledDropdownField(
                        label = "",
                        value = state.statusFilter.displayLabel().let { if (it == "All") "All Statuses" else it },
                        options = listOf("Active", "Inactive"),
                        onSelected = { selected ->
                            onEvent(
                                SchoolConfigUiEvent.StatusFilterChanged(selected.toSchoolConfigStatusFilter())
                            )
                        },
                        modifier = Modifier.width(160.dp),
                        allOptionLabel = "All Statuses"
                    )
                }
            ) {
                AdminListToolbarButton(
                    label = "Reset",
                    icon = painterResource(Res.drawable.refresh_cw),
                    onClick = { onEvent(SchoolConfigUiEvent.FiltersReset) },
                    width = 112.dp,
                    borderColor = SchoolConfigBlue,
                    contentColor = SchoolConfigBlue
                )
            }
        },
        table = { tableModifier ->
            AdminListTableCard(
                modifier = tableModifier,
                minContentWidth = 960.dp,
                borderColor = SchoolConfigBorder,
                header = {
                    AdminListSortHeaderRow(
                        columns = listOf(
                            AdminListSortColumn("School Name", 1.5f, state.sortField == SchoolConfigSortField.SCHOOL_NAME, state.sortAscending) { onEvent(SchoolConfigUiEvent.SortChanged(SchoolConfigSortField.SCHOOL_NAME)) },
                            AdminListSortColumn("Code", 1f, state.sortField == SchoolConfigSortField.CODE, state.sortAscending) { onEvent(SchoolConfigUiEvent.SortChanged(SchoolConfigSortField.CODE)) },
                            AdminListSortColumn("City", 1f, state.sortField == SchoolConfigSortField.CITY, state.sortAscending) { onEvent(SchoolConfigUiEvent.SortChanged(SchoolConfigSortField.CITY)) },
                            AdminListSortColumn("Updated Date", 1f, state.sortField == SchoolConfigSortField.UPDATED_DATE, state.sortAscending) { onEvent(SchoolConfigUiEvent.SortChanged(SchoolConfigSortField.UPDATED_DATE)) },
                            AdminListSortColumn("Status", 0.9f, state.sortField == SchoolConfigSortField.STATUS, state.sortAscending) { onEvent(SchoolConfigUiEvent.SortChanged(SchoolConfigSortField.STATUS)) }
                        ),
                        actionsLabel = "Actions",
                        actionsWeight = 0.95f,
                        horizontalPadding = 24.dp
                    )
                },
                body = {
                    if (state.isLoading) {
                        AdminListTableShimmer(
                            columns = listOf(
                                AdminListHeaderColumn("School Name", 1.5f),
                                AdminListHeaderColumn("Code", 1f),
                                AdminListHeaderColumn("City", 1f),
                                AdminListHeaderColumn("Updated Date", 1f),
                                AdminListHeaderColumn("Status", 0.9f),
                                AdminListHeaderColumn("Actions", 0.95f)
                            ),
                            rowCount = 6,
                            modifier = Modifier.fillMaxWidth().weight(1f),
                            horizontalPadding = 24.dp
                        )
                    } else if (state.rows.isEmpty()) {
                        Box(Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) {
                            AppText("No schools found", color = SchoolConfigNavy, fontWeight = FontWeight.Bold)
                        }
                    } else {
                        AdminListTableBody(items = state.rows, key = { it.id }) { row ->
                            SchoolConfigTableRow(
                                row = row,
                                showDivider = row.id != state.rows.lastOrNull()?.id,
                                onView = { onEvent(SchoolConfigUiEvent.ViewClicked(row)) },
                                onEdit = { onEvent(SchoolConfigUiEvent.EditClicked(row)) }
                            )
                        }
                    }
                },
                footer = {
                    AdminListFooter(
                        pageText = pageFooterText(window, "schools"),
                        pageIndex = window.pageIndex,
                        pageCount = window.pageCount,
                        onPageChanged = { onEvent(SchoolConfigUiEvent.PageChanged(it)) },
                        pageSize = state.pageSize,
                        rowsPerPageExpanded = rowsPerPageExpanded,
                        onRowsPerPageToggle = { rowsPerPageExpanded = !rowsPerPageExpanded },
                        onRowsPerPageDismiss = { rowsPerPageExpanded = false },
                        onPageSizeChanged = { onEvent(SchoolConfigUiEvent.PageSizeChanged(it)) },
                        horizontalPadding = 24.dp,
                        footerColor = SchoolConfigMuted,
                        borderColor = SchoolConfigBorder,
                        primaryColor = SchoolConfigBlue,
                        navyColor = SchoolConfigNavy
                    )
                }
            )
        },
        bottomAction = { Spacer(Modifier.weight(1f)) }
    )
    state.viewingRow?.let { row ->
        SchoolConfigViewModal(
            row = row,
            config = state.viewingConfig,
            onClose = { onEvent(SchoolConfigUiEvent.ViewDismissed) }
        )
    }
    state.editingRow?.let { row ->
        SchoolConfigEditDialog(
            row = row,
            config = state.editingConfig,
            isSaving = state.isSaving,
            onDismiss = { onEvent(SchoolConfigUiEvent.EditDismissed) },
            onSave = { onEvent(SchoolConfigUiEvent.EditSaved(it)) },
            onAcademicYearSelected = { onEvent(SchoolConfigUiEvent.AcademicYearSelected(it.id, it.label)) },
            onDownloadHolidayTemplate = { onEvent(SchoolConfigUiEvent.HolidayTemplateRequested) },
            onImportHolidays = { fileName, bytes -> onEvent(SchoolConfigUiEvent.HolidayImportRequested(fileName, bytes)) },
            onOperationFailed = { onEvent(SchoolConfigUiEvent.OperationFailed(it)) }
        )
    }
    }
}

@Composable
private fun SchoolConfigTableRow(
    row: SchoolConfigRow,
    showDivider: Boolean,
    onView: () -> Unit,
    onEdit: () -> Unit
) {
    Column(Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .onViewDoubleClick(onView)
                .height(52.dp)
                .padding(horizontal = 24.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            AdminListTableBodyText(row.schoolName, Modifier.weight(1.5f), color = SchoolConfigNavy, fontWeight = FontWeight.SemiBold)
            AdminListTableBodyText(row.code, Modifier.weight(1f), color = SchoolConfigNavy)
            AdminListTableBodyText(row.city, Modifier.weight(1f), color = SchoolConfigNavy)
            AdminListTableBodyText(row.updatedDate, Modifier.weight(1f), color = SchoolConfigNavy)
            Box(Modifier.weight(0.9f)) {
                AdminStatusChip(
                    label = if (row.isActive) "Active" else "Inactive",
                    contentColor = if (row.isActive) AppSuccess else AppDanger,
                    backgroundColor = Color.Transparent
                )
            }
            Box(Modifier.weight(.95f), contentAlignment = Alignment.CenterStart) {
                Row(horizontalArrangement = Arrangement.spacedBy(2.dp), verticalAlignment = Alignment.CenterVertically) {
                    IconButton(onClick = onView, modifier = Modifier.size(32.dp)) {
                        Icon(
                            painter = painterResource(Res.drawable.eye),
                            contentDescription = "View ${row.schoolName}",
                            tint = SchoolConfigNavy,
                            modifier = Modifier.size(16.dp)
                        )
                    }
                    IconButton(onClick = onEdit, modifier = Modifier.size(32.dp)) {
                        Icon(
                            painter = painterResource(Res.drawable.edit),
                            contentDescription = "Edit ${row.schoolName}",
                            tint = SchoolConfigNavy,
                            modifier = Modifier.size(16.dp)
                        )
                    }
                }
            }
        }
        if (showDivider) {
            Box(Modifier.fillMaxWidth().height(1.dp).background(SchoolConfigBorder))
        }
    }
}
